import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { FC, useEffect, useState } from 'react';

import { PaymentMethodHolder, PaymentRequestInfo, Person } from '../gql-types.generated';
import LoadingMask from './LoadingMask';
import { ApteanPaySDK, ApteanPaySDKBankComponent, useApteanPay } from '../util/ApteanPay';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    padding: {
      padding: theme.spacing(2),
    },
    iFramePadding: {
      padding: theme.spacing(0, 0, 1.5, 0),
      minHeight: 208,
    },
    iFramePaddingGuest: {
      padding: theme.spacing(0, 0, 1.5, 0),
      position: 'relative',
      minHeight: 208,
    },
    paymentAction: {
      marginTop: 6,
      marginLeft: 4,
    },
    bankInformationWrap: {
      padding: theme.spacing(1, 1.5, 0, 1.5),
      flexGrow: 1,
    },
    holderName: {
      padding: theme.spacing(0, 2, 1, 2),
    },
    holderNameFieldError: {
      '& ::placeholder': {
        color: theme.palette.error.main,
        opacity: 1,
      },
    },
    bankIframe: {
      padding: theme.spacing(0, 2),
      minHeight: 65,
    },
  }),
);

interface BankInfoProps {
  backText?: string;
  bankError?: string;
  setBankError?: (message?: string) => void;
  handleBack?: () => void;
  onTokenizeInjectSuccess?: (apteanPay: ApteanPaySDK, bankComponent: ApteanPaySDKBankComponent) => void;
  viewerUser?: Person;
  paymentRequestInfo?: PaymentRequestInfo;
  isIFrameVisible: boolean;
  setIsIFrameVisible: (isIFrameVisible: boolean) => void;
  isGuestUser?: boolean;
  setSendingPayment?: (sendingPayment: boolean) => void;
  sendingPayment?: boolean;
  submitText?: string;
  bankInfoDisabled?: boolean;
  merchantTenantId?: string;
  customerId?: string;
  holderInformation?: PaymentMethodHolder | null;
  onTokenize?: (apteanPay: ApteanPaySDK, bankComponent: ApteanPaySDKBankComponent) => void;
}

const BankInfo: FC<BankInfoProps> = props => {
  const classes = useStyles();
  const {
    backText,
    bankError,
    handleBack,
    onTokenizeInjectSuccess,
    viewerUser,
    isIFrameVisible,
    setIsIFrameVisible,
    isGuestUser,
    setSendingPayment,
    sendingPayment,
    submitText,
    merchantTenantId,
    customerId,
    onTokenize,
  } = props;
  const { apteanPay, sdkComponent, configure, mountComponent } = useApteanPay();
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (merchantTenantId) {
      configure(merchantTenantId, customerId, !!isGuestUser, 'bank');
    }
  }, [merchantTenantId, customerId]);

  useEffect(() => {
    if (apteanPay && sdkComponent) {
      setShowLoading(true);
      setIsIFrameVisible(true);
      mountComponent('js_sdk_bank', '#js_sdk_bank_button', () => {
        setShowLoading(false);
      });
      if (onTokenizeInjectSuccess) {
        onTokenizeInjectSuccess(apteanPay, sdkComponent as ApteanPaySDKBankComponent);
      }
    }
  }, [sdkComponent]);

  // allow making the bank payment if the user is logged with billing information or is doing a guest checkout
  const enablePayment = !!viewerUser?.payerProfile || !viewerUser;

  const onBankSubmit = () => {
    // Skip holder name validation for bank - the bank SDK handles the account holder name
    // internally via its own inline field (checkname).
    if (onTokenize && apteanPay && sdkComponent) {
      if (setSendingPayment) setSendingPayment(true);
      onTokenize(apteanPay, sdkComponent as ApteanPaySDKBankComponent);
    }
  };

  return (
    <Box>
      <LoadingMask loading={showLoading} />
      <Grid container justifyContent="space-between" className={classes.bankInformationWrap}>
        <Grid item>
          <Typography id="bank-account-information" fontWeight="bold">
            {' '}
            Add Bank Account
          </Typography>
        </Grid>
      </Grid>
      <Box className={isGuestUser ? classes.iFramePaddingGuest : classes.iFramePadding}>
        {isGuestUser === true && <LoadingMask loading={!isIFrameVisible} />}
        <Box id="js_sdk_bank" className={classes.bankIframe} />
      </Box>
      {(onTokenize || handleBack) && (
        <Grid container justifyContent="space-between" className={classes.padding}>
          {handleBack && (
            <Button
              className={classes.paymentAction}
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                handleBack();
              }}
              data-cy="back-to-billing"
            >
              {backText || 'Back'}
            </Button>
          )}
          {onTokenize && (
            <Button
              className={classes.paymentAction}
              variant="contained"
              color="primary"
              onClick={() => {
                onBankSubmit();
              }}
              data-cy="make-bank-payment"
              disabled={!enablePayment || sendingPayment}
              id="js_sdk_bank_button"
            >
              {submitText || 'Make Payment'}
            </Button>
          )}
        </Grid>
      )}
      {bankError && (
        <Grid container justifyContent="space-between" className={classes.padding}>
          <Typography color="error" variant="subtitle2">
            {bankError}
          </Typography>
        </Grid>
      )}
    </Box>
  );
};

export default BankInfo;
